import {
  ModuleBrand,
  SolarModule,
  InverterBrand,
  Inverter,
  InverterType,
  Optimizer,
  PanelOrientation,
} from '../types';
import { DISPLAY_SYSTEM_COUNT, MAX_STRUCTURES } from '../constants';
import { SystemKey, SYSTEM_KEYS } from '../constants/aurora.constants';
import { SystemEquipment } from '../store/slices/createCRSlice';

// --- Types ---

export interface AuroraBomItem {
  id: string;
  component_type: string;
  sku: string | null;
  name: string;
  manufacturer_name: string;
  quantity: number;
}

export type BomComponentType =
  | 'modules'
  | 'inverters'
  | 'microinverters'
  | 'dc_optimizers'
  | 'combiner_boxes';

export interface SystemProposal {
  modules: AuroraBomItem[];
  inverters: AuroraBomItem[];
  microinverters: AuroraBomItem[];
  optimizers: AuroraBomItem[];
  combiners: AuroraBomItem[];
}

export interface AuroraSystemConfig {
  systemCount: number;
  systems: SystemProposal[];
  production: number | null;
}

export interface StructureRoofFace {
  area: number;
  modules_area: number;
  number_of_modules: number;
  azimuth: number;
  pitch: number;
}

export interface StructureRoofDetail {
  structure_name: string;
  total_area: number;
  modules_area: number;
  faces: StructureRoofFace[];
  non_module_faces_count: number;
  non_module_faces_area: number;
}

export enum MatchConfidence {
  EXACT = 'exact',
  FUZZY = 'fuzzy',
  NONE = 'none',
}

// --- String normalization ---

const CORPORATE_SUFFIXES = /\b(inc\.?|corp\.?|llc\.?|ltd\.?|co\.?|company|corporation|limited|technologies)\b/gi;

function normalizeString(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(CORPORATE_SUFFIXES, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function fuzzyMatch(a: string, b: string): boolean {
  const na = normalizeString(a);
  const nb = normalizeString(b);
  return na === nb || na.includes(nb) || nb.includes(na);
}

// --- System proposal builder ---

const EMPTY_SYSTEM: SystemProposal = {
  modules: [],
  inverters: [],
  microinverters: [],
  optimizers: [],
  combiners: [],
};

function createEmptySystem(): SystemProposal {
  return { ...EMPTY_SYSTEM, modules: [], inverters: [], microinverters: [], optimizers: [], combiners: [] };
}

function isSolarEdgeFamily(manufacturerName: string): boolean {
  return normalizeString(manufacturerName).includes('solaredge');
}

export function buildSystemConfig(designSummary: Record<string, any>): AuroraSystemConfig | null {
  const design = designSummary?.design;
  if (!design) return null;

  const bom: AuroraBomItem[] = Array.isArray(design.bill_of_materials) ? design.bill_of_materials : [];
  if (bom.length === 0) return null;

  const production = design.energy_production?.annual ?? null;

  // Group by component_type
  const modules = bom.filter(i => i.component_type === 'modules');
  const inverters = bom.filter(i => i.component_type === 'inverters');
  const microinverters = bom.filter(i => i.component_type === 'microinverters');
  const optimizers = bom.filter(i => i.component_type === 'dc_optimizers');
  const combiners = bom.filter(i => i.component_type === 'combiner_boxes');

  // Group inverter BOM items by (component_type + manufacturer_name)
  // Same type + same manufacturer = same group → same system
  // Different type or different manufacturer = different group → different system
  interface InverterGroup {
    componentType: string;
    manufacturerName: string;
    items: AuroraBomItem[];
  }
  const inverterGroups: InverterGroup[] = [];
  [...inverters, ...microinverters].forEach(item => {
    const existing = inverterGroups.find(g =>
      g.componentType === item.component_type &&
      fuzzyMatch(g.manufacturerName, item.manufacturer_name)
    );
    if (existing) {
      existing.items.push(item);
    } else {
      inverterGroups.push({
        componentType: item.component_type,
        manufacturerName: item.manufacturer_name,
        items: [item],
      });
    }
  });

  const systemCount = Math.max(1, Math.min(Math.max(modules.length, inverterGroups.length), DISPLAY_SYSTEM_COUNT));
  const systems: SystemProposal[] = Array.from({ length: systemCount }, createEmptySystem);

  // 1. Assign modules: 1 per system
  modules.slice(0, systemCount).forEach((mod, i) => {
    systems[i].modules.push(mod);
  });

  // 2. Assign inverter groups: 1 group per system
  inverterGroups.slice(0, systemCount).forEach((group, idx) => {
    const isMicro = group.componentType === 'microinverters';
    group.items.forEach(item => {
      if (isMicro) {
        systems[idx].microinverters.push(item);
      } else {
        systems[idx].inverters.push(item);
      }
    });
  });

  // Extra groups beyond systemCount: match by manufacturer or default to first available
  inverterGroups.slice(systemCount).forEach(group => {
    const isMicro = group.componentType === 'microinverters';
    const targetIdx = systems.findIndex(s => {
      const sysItems = [...s.inverters, ...s.microinverters];
      return sysItems.some(inv => fuzzyMatch(inv.manufacturer_name, group.manufacturerName));
    });
    const dest = targetIdx >= 0 ? targetIdx : 0;
    group.items.forEach(item => {
      if (isMicro) {
        systems[dest].microinverters.push(item);
      } else {
        systems[dest].inverters.push(item);
      }
    });
  });

  // 3. Assign optimizers: to system with SolarEdge inverter
  optimizers.forEach(opt => {
    const seIdx = systems.findIndex(s =>
      s.inverters.some(inv => isSolarEdgeFamily(inv.manufacturer_name))
    );
    systems[seIdx >= 0 ? seIdx : 0].optimizers.push(opt);
  });

  // 4. Assign combiners: to system with microinverter
  combiners.forEach(comb => {
    const miIdx = systems.findIndex(s => s.microinverters.length > 0);
    systems[miIdx >= 0 ? miIdx : 0].combiners.push(comb);
  });

  return { systemCount, systems, production };
}

// --- Model number normalization ---

/**
 * Extracts the core model identifier from an Aurora or DB model string.
 * Strips parenthetical content (e.g. "(240V)"), common suffixes ("Hub"),
 * and trailing SKU fragments to improve matching.
 *
 * "SE11400H-US (240V) Hub" → "se11400h-us"
 * "SE11400H-US000BNU4"     → "se11400h-us000bnu4"
 * "REC-AA 405 Pure Black"  → "rec-aa 405 pure black"
 */
function extractCoreModel(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/\s*\([^)]*\)\s*/g, ' ')  // remove parenthetical (e.g. "(240V)")
    .replace(/\b(hub|gateway|kit)\b/gi, '')  // remove common Aurora suffixes
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Extracts all numeric tokens from a string.
 * "SE11400H-US (240V) Hub" → ["11400", "240"]
 */
function extractNumbers(s: string): string[] {
  return s.match(/\d+/g) ?? [];
}

/**
 * Strips non-alphanumeric characters for prefix comparison.
 */
function stripNonAlnum(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '');
}

const MIN_MODEL_PREFIX_LENGTH = 6;

/**
 * Scores how well a candidate model matches an Aurora name.
 * Higher score = better match. Returns 0 for no meaningful match.
 */
function scoreModelMatch(auroraName: string, candidateModel: string): number {
  const auroraAlnum = stripNonAlnum(auroraName);
  const candidateAlnum = stripNonAlnum(candidateModel);

  if (auroraAlnum.length < MIN_MODEL_PREFIX_LENGTH || candidateAlnum.length < MIN_MODEL_PREFIX_LENGTH) return 0;

  // Compute shared prefix length
  let prefixLen = 0;
  const maxPrefix = Math.min(auroraAlnum.length, candidateAlnum.length);
  while (prefixLen < maxPrefix && auroraAlnum[prefixLen] === candidateAlnum[prefixLen]) {
    prefixLen++;
  }
  if (prefixLen < MIN_MODEL_PREFIX_LENGTH) return 0;

  let score = prefixLen * 10;

  // Bonus: matching numeric tokens (e.g. voltage "240" in both strings)
  const auroraNumbers = extractNumbers(auroraName);
  const candidateNumbers = extractNumbers(candidateModel);
  const matchingNumbers = auroraNumbers.filter(n => candidateNumbers.includes(n));
  score += matchingNumbers.length * 5;

  // Bonus: prefer candidates closer in length to the core Aurora name (less SKU noise)
  const coreName = extractCoreModel(auroraName);
  const coreCandidate = extractCoreModel(candidateModel);
  const lengthDiff = Math.abs(coreName.length - coreCandidate.length);
  score -= lengthDiff;

  return score;
}

/**
 * From a list of candidates, returns the best scoring match.
 * Returns null if no candidate scores above 0 or if the top two scores are tied (ambiguous).
 * When `allowTied` is true, returns the best candidate even if tied with runner-up (for suggestions).
 */
function pickBestMatch<T>(
  candidates: T[],
  getModel: (item: T) => string,
  auroraName: string,
  allowTied = false,
): T | null {
  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0];

  const scored = candidates
    .map(item => ({ item, score: scoreModelMatch(auroraName, getModel(item)) }))
    .filter(s => s.score > 0)
    .sort((a, b) => b.score - a.score);

  if (scored.length === 0) return null;
  if (scored.length === 1) return scored[0].item;

  // Only return if the best score is clearly better than the runner-up
  if (scored[0].score > scored[1].score) return scored[0].item;

  return allowTied ? scored[0].item : null;
}

// --- DB matching helpers ---

export function matchModuleBrand(
  manufacturerName: string,
  brands: ModuleBrand[]
): { brand: ModuleBrand | null; confidence: MatchConfidence } {
  const normalized = normalizeString(manufacturerName);

  const exact = brands.find(b => normalizeString(b.name) === normalized);
  if (exact) return { brand: exact, confidence: MatchConfidence.EXACT };

  const fuzzy = brands.find(b => {
    const bn = normalizeString(b.name);
    return normalized.includes(bn) || bn.includes(normalized);
  });
  if (fuzzy) return { brand: fuzzy, confidence: MatchConfidence.FUZZY };

  return { brand: null, confidence: MatchConfidence.NONE };
}

export function matchModule(
  auroraName: string,
  modules: SolarModule[]
): SolarModule | null {
  const n = auroraName.toLowerCase().trim();

  // 1. Exact match
  const exact = modules.find(m => m.modelNumber.toLowerCase().trim() === n);
  if (exact) return exact;

  // 2. Substring containment — collect all candidates
  const substringCandidates = modules.filter(m => {
    const model = m.modelNumber.toLowerCase().trim();
    const full = m.fullName.toLowerCase().trim();
    return model.includes(n) || n.includes(model) || full.includes(n) || n.includes(full);
  });
  if (substringCandidates.length === 1) return substringCandidates[0];
  if (substringCandidates.length > 1) {
    return pickBestMatch(substringCandidates, m => m.modelNumber, auroraName);
  }

  // 3. Core model match — collect all candidates
  const coreAurora = extractCoreModel(auroraName);
  const coreCandidates = modules.filter(m => {
    const coreModel = extractCoreModel(m.modelNumber);
    const coreFull = extractCoreModel(m.fullName);
    return coreModel.includes(coreAurora) || coreAurora.includes(coreModel)
      || coreFull.includes(coreAurora) || coreAurora.includes(coreFull);
  });
  if (coreCandidates.length === 1) return coreCandidates[0];
  if (coreCandidates.length > 1) {
    return pickBestMatch(coreCandidates, m => m.modelNumber, auroraName);
  }

  // 4. Prefix-based — score all modules and pick the best
  return pickBestMatch(modules, m => m.modelNumber, auroraName);
}

export function matchInverterBrand(
  manufacturerName: string,
  componentType: string,
  inverterBrands: InverterBrand[]
): { brand: InverterBrand | null; confidence: MatchConfidence } {
  const invType = componentType === 'microinverters' ? InverterType.MICROINVERTER : InverterType.INVERTER;
  const filtered = inverterBrands.filter(b => b.inverter_type === invType);
  const normalized = normalizeString(manufacturerName);

  const exact = filtered.find(b => normalizeString(b.name) === normalized);
  if (exact) return { brand: exact, confidence: MatchConfidence.EXACT };

  const fuzzy = filtered.find(b => {
    const bn = normalizeString(b.name);
    return normalized.includes(bn) || bn.includes(normalized);
  });
  if (fuzzy) return { brand: fuzzy, confidence: MatchConfidence.FUZZY };

  return { brand: null, confidence: MatchConfidence.NONE };
}

export function matchInverterModel(
  auroraName: string,
  inverters: Inverter[]
): Inverter | null {
  const n = auroraName.toLowerCase().trim();

  // 1. Exact match
  const exact = inverters.find(inv => inv.model.toLowerCase().trim() === n);
  if (exact) return exact;

  // 2. Substring containment — collect all candidates
  const substringCandidates = inverters.filter(inv => {
    const m = inv.model.toLowerCase().trim();
    return m.includes(n) || n.includes(m);
  });
  if (substringCandidates.length === 1) return substringCandidates[0];
  if (substringCandidates.length > 1) {
    return pickBestMatch(substringCandidates, inv => inv.model, auroraName);
  }

  // 3. Core model match — collect all candidates
  const coreAurora = extractCoreModel(auroraName);
  const coreCandidates = inverters.filter(inv => {
    const coreModel = extractCoreModel(inv.model);
    return coreModel.includes(coreAurora) || coreAurora.includes(coreModel);
  });
  if (coreCandidates.length === 1) return coreCandidates[0];
  if (coreCandidates.length > 1) {
    return pickBestMatch(coreCandidates, inv => inv.model, auroraName);
  }

  // 4. Prefix-based — score all inverters and pick the best
  return pickBestMatch(inverters, inv => inv.model, auroraName);
}

export function matchOptimizer(
  auroraName: string,
  optimizers: Optimizer[]
): Optimizer | null {
  const n = auroraName.toLowerCase().trim();

  // 1. Exact match
  const exact = optimizers.find(o => o.type.toLowerCase().trim() === n);
  if (exact) return exact;

  // 2. Substring containment — collect all candidates
  const substringCandidates = optimizers.filter(o => {
    const t = o.type.toLowerCase().trim();
    return t.includes(n) || n.includes(t);
  });
  if (substringCandidates.length === 1) return substringCandidates[0];
  if (substringCandidates.length > 1) {
    return pickBestMatch(substringCandidates, o => o.type, auroraName);
  }

  // 3. Core model match — collect all candidates
  const coreAurora = extractCoreModel(auroraName);
  const coreCandidates = optimizers.filter(o => {
    const coreType = extractCoreModel(o.type);
    return coreType.includes(coreAurora) || coreAurora.includes(coreType);
  });
  if (coreCandidates.length === 1) return coreCandidates[0];
  if (coreCandidates.length > 1) {
    return pickBestMatch(coreCandidates, o => o.type, auroraName);
  }

  // 4. Prefix-based — score all optimizers and pick the best
  return pickBestMatch(optimizers, o => o.type, auroraName);
}

// --- BOM item match results (for review modal) ---

export interface ModuleMatchResult {
  bomItem: AuroraBomItem;
  matchedBrand: ModuleBrand | null;
  matchedModule: SolarModule | null;
  confidence: MatchConfidence;
}

export interface InverterMatchResult {
  bomItem: AuroraBomItem;
  componentType: 'inverters' | 'microinverters';
  matchedBrand: InverterBrand | null;
  matchedInverter: Inverter | null;
  confidence: MatchConfidence;
}

export interface OptimizerMatchResult {
  bomItem: AuroraBomItem;
  matchedOptimizer: Optimizer | null;
  confidence: MatchConfidence;
}

export interface BomMatchResults {
  modules: ModuleMatchResult[];
  inverters: InverterMatchResult[];
  optimizers: OptimizerMatchResult[];
  combiners: AuroraBomItem[];
}

/**
 * Matches all BOM items from the system config against the DB data.
 * For non-matches, provides the best suggestion (even if ambiguous).
 */
export function matchAllBomItems(
  config: AuroraSystemConfig,
  data: AuroraMatchingData,
): BomMatchResults {
  const seen = new Set<string>();
  const allModules: AuroraBomItem[] = [];
  const allInverters: { item: AuroraBomItem; componentType: 'inverters' | 'microinverters' }[] = [];
  const allOptimizers: AuroraBomItem[] = [];
  const allCombiners: AuroraBomItem[] = [];

  config.systems.forEach(sys => {
    sys.modules.forEach(m => { if (!seen.has(m.id)) { seen.add(m.id); allModules.push(m); } });
    sys.inverters.forEach(i => { if (!seen.has(i.id)) { seen.add(i.id); allInverters.push({ item: i, componentType: 'inverters' }); } });
    sys.microinverters.forEach(i => { if (!seen.has(i.id)) { seen.add(i.id); allInverters.push({ item: i, componentType: 'microinverters' }); } });
    sys.optimizers.forEach(o => { if (!seen.has(o.id)) { seen.add(o.id); allOptimizers.push(o); } });
    sys.combiners.forEach(c => { if (!seen.has(c.id)) { seen.add(c.id); allCombiners.push(c); } });
  });

  const modules: ModuleMatchResult[] = allModules.map(bomItem => {
    const brandMatch = matchModuleBrand(bomItem.manufacturer_name, data.moduleBrands);
    const brandModules = brandMatch.brand
      ? data.solarModules.filter(m => m.brands.some(b => b.id === brandMatch.brand!.id))
      : data.solarModules;
    let moduleMatch = matchModule(bomItem.name, brandModules);
    if (!moduleMatch) {
      moduleMatch = pickBestMatch(brandModules, m => m.modelNumber, bomItem.name, true)
        ?? pickBestMatch(data.solarModules, m => m.modelNumber, bomItem.name, true);
    }
    const isExact = moduleMatch?.modelNumber.toLowerCase().trim() === bomItem.name.toLowerCase().trim();
    const confidence = moduleMatch ? (isExact ? MatchConfidence.EXACT : MatchConfidence.FUZZY) : MatchConfidence.NONE;
    return { bomItem, matchedBrand: brandMatch.brand, matchedModule: moduleMatch, confidence };
  });

  const inverters: InverterMatchResult[] = allInverters.map(({ item, componentType }) => {
    const brandMatch = matchInverterBrand(item.manufacturer_name, componentType, data.inverterBrands);
    const brandInverters = brandMatch.brand
      ? data.inverters.filter(inv => inv.manufacturer_id === brandMatch.brand!.id)
      : data.inverters;
    let inverterMatch = matchInverterModel(item.name, brandInverters);
    if (!inverterMatch) {
      inverterMatch = pickBestMatch(brandInverters, inv => inv.model, item.name, true)
        ?? pickBestMatch(data.inverters, inv => inv.model, item.name, true);
    }
    const isExact = inverterMatch?.model.toLowerCase().trim() === item.name.toLowerCase().trim();
    const confidence = inverterMatch ? (isExact ? MatchConfidence.EXACT : MatchConfidence.FUZZY) : MatchConfidence.NONE;
    return { bomItem: item, componentType, matchedBrand: brandMatch.brand, matchedInverter: inverterMatch, confidence };
  });

  const optimizers: OptimizerMatchResult[] = allOptimizers.map(bomItem => {
    let optMatch = matchOptimizer(bomItem.name, data.optimizers);
    let isExact = optMatch?.type.toLowerCase().trim() === bomItem.name.toLowerCase().trim();
    if (!optMatch) {
      optMatch = pickBestMatch(data.optimizers, o => o.type, bomItem.name, true);
      isExact = false;
    }
    return {
      bomItem,
      matchedOptimizer: optMatch,
      confidence: optMatch ? (isExact ? MatchConfidence.EXACT : MatchConfidence.FUZZY) : MatchConfidence.NONE,
    };
  });

  return { modules, inverters, optimizers, combiners: allCombiners };
}

// --- Config-to-payload mapping ---

export interface AuroraMatchingData {
  moduleBrands: ModuleBrand[];
  solarModules: SolarModule[];
  inverterBrands: InverterBrand[];
  inverters: Inverter[];
  optimizers: Optimizer[];
}

export interface AuroraSystemConfigPayload {
  systems: Partial<Record<SystemKey, Partial<SystemEquipment>>>;
  production?: string;
}

/** Pre-reviewed match selections from the item review modal */
export interface ReviewedItemSelections {
  modules: Map<string, { brand: ModuleBrand | null; module: SolarModule | null }>;
  inverters: Map<string, { brand: InverterBrand | null; inverter: Inverter | null; componentType: 'inverters' | 'microinverters' }>;
  optimizers: Map<string, { optimizer: Optimizer | null }>;
  removedIds: Set<string>;
}

/**
 * Builds system config payload using pre-reviewed matches from Step 1.
 * No re-matching — uses the user's confirmed selections.
 */
export function buildPayloadFromReviewed(
  config: AuroraSystemConfig,
  reviewed: ReviewedItemSelections,
): AuroraSystemConfigPayload {
  const systems: Partial<Record<SystemKey, Partial<SystemEquipment>>> = {};

  config.systems.forEach((sys, idx) => {
    if (idx >= SYSTEM_KEYS.length) return;
    const key = SYSTEM_KEYS[idx];
    const equipment: Partial<SystemEquipment> = {};

    // Module
    const moduleBom = sys.modules.find(m => !reviewed.removedIds.has(m.id));
    if (moduleBom) {
      const sel = reviewed.modules.get(moduleBom.id);
      equipment.module = {
        selected_brand: sel?.brand?.name ?? '',
        selected_module: sel?.module ? {
          fullName: sel.module.fullName,
          wattage: sel.module.wattage,
          modelNumber: sel.module.modelNumber,
          width: sel.module.width,
          height: sel.module.height,
        } : {},
        quantity: moduleBom.quantity,
      };
    }

    // Inverters
    const invItems = [...sys.inverters, ...sys.microinverters].filter(i => !reviewed.removedIds.has(i.id));
    if (invItems.length > 0) {
      const firstSel = reviewed.inverters.get(invItems[0].id);
      const invType = firstSel?.componentType === 'microinverters' ? InverterType.MICROINVERTER : InverterType.INVERTER;

      const selectedInverters = invItems.map(bomItem => {
        const sel = reviewed.inverters.get(bomItem.id);
        return {
          model: sel?.inverter?.model ?? bomItem.name,
          wattage: sel?.inverter?.wattage,
          current: sel?.inverter?.current,
          quantity: bomItem.quantity,
        };
      });

      equipment.inverters = {
        selected_type: invType,
        selected_brand: firstSel?.brand ? {
          name: firstSel.brand.name,
          inverter_type: firstSel.brand.inverter_type,
        } : undefined,
        selected_inverters: selectedInverters,
        optimizer_quantity: 0,
        combiner: '',
      };

      // Optimizer
      const optBom = sys.optimizers.find(o => !reviewed.removedIds.has(o.id));
      if (optBom) {
        const optSel = reviewed.optimizers.get(optBom.id);
        equipment.inverters.selected_optimizer = {
          type: optSel?.optimizer?.type ?? optBom.name,
          current: optSel?.optimizer?.current,
        };
        equipment.inverters.optimizer_quantity = optBom.quantity;
      }

      // Combiner
      const combBom = sys.combiners.find(c => !reviewed.removedIds.has(c.id));
      if (combBom) {
        equipment.inverters.combiner = combBom.name;
      }
    }

    systems[key] = equipment;
  });

  const production = config.production != null
    ? String(Math.round(config.production))
    : undefined;

  return { systems, production };
}

/**
 * Removes BOM items from the system config by their IDs.
 * Returns a new config with matching items filtered out.
 */
export function filterSystemConfig(
  config: AuroraSystemConfig,
  removedIds: Set<string>,
): AuroraSystemConfig {
  const systems = config.systems.map(sys => ({
    modules: sys.modules.filter(m => !removedIds.has(m.id)),
    inverters: sys.inverters.filter(i => !removedIds.has(i.id)),
    microinverters: sys.microinverters.filter(i => !removedIds.has(i.id)),
    optimizers: sys.optimizers.filter(o => !removedIds.has(o.id)),
    combiners: sys.combiners.filter(c => !removedIds.has(c.id)),
  }));
  const systemCount = systems.filter(s =>
    s.modules.length > 0 || s.inverters.length > 0 || s.microinverters.length > 0 ||
    s.optimizers.length > 0 || s.combiners.length > 0
  ).length || 1;
  return { systemCount, systems, production: config.production };
}

// --- Structures / Roof Design extraction ---

/** Sort structures: with faces first (by total area desc), empty ones last. Renumber sequentially. */
export function sortStructures(list: StructureRoofDetail[]): StructureRoofDetail[] {
  const sorted = [...list].sort((a, b) => {
    const aHasFaces = a.faces.length > 0 ? 0 : 1;
    const bHasFaces = b.faces.length > 0 ? 0 : 1;
    if (aHasFaces !== bHasFaces) return aHasFaces - bHasFaces;
    return (b.total_area || 0) - (a.total_area || 0);
  });
  return sorted.map((s, i) => ({ ...s, structure_name: `STRUCTURE ${i + 1}` }));
}

export function isValidStructureNumericValue(value: number | undefined): boolean {
  return value !== undefined && !isNaN(value);
}


export function extractStructuresRoofDetails(
  roofDesignSummary: Record<string, any> | null,
  designSummary: Record<string, any> | null,
): StructureRoofDetail[] {
  const roofs: any[] = roofDesignSummary?.roofs;
  if (!Array.isArray(roofs) || roofs.length === 0) return [];

  if (roofs.length > MAX_STRUCTURES) {
    console.warn(
      `Aurora roof_design contains ${roofs.length} roofs; only the first ${MAX_STRUCTURES} will be used.`,
    );
  }

  const arrays: any[] = designSummary?.design?.arrays ?? [];

  // Group arrays by azimuth-pitch, then sub-group by face to get exact counts
  // e.g. azimuth 180, pitch 26 → face 3: 11, face 4: 9, face 5: 5
  const azPitchGroups = new Map<string, Map<number, number>>();
  for (const arr of arrays) {
    const azPitchKey = `${Math.round(arr.azimuth)}-${Math.round(arr.pitch)}`;
    const faceId = arr.face as number;
    const count = arr.module?.count ?? 0;
    if (!azPitchGroups.has(azPitchKey)) {
      azPitchGroups.set(azPitchKey, new Map<number, number>());
    }
    const faceMap = azPitchGroups.get(azPitchKey)!;
    faceMap.set(faceId, (faceMap.get(faceId) ?? 0) + count);
  }

  const details = roofs.slice(0, MAX_STRUCTURES).map((roof) => {
    const allFaces: any[] = Array.isArray(roof.faces) ? roof.faces : [];
    const nonModuleFaces = allFaces.filter((f: any) => !f.modules);
    const nonModuleFacesArea = nonModuleFaces.reduce((sum: number, f: any) => sum + (f.area ?? 0), 0);

    const faces: StructureRoofFace[] = allFaces
      .filter((f: any) => f.modules === true)
      .map((face: any) => ({
        area: face.area ?? 0,
        modules_area: face.modules_area ?? 0,
        number_of_modules: 0,
        azimuth: Math.round(face.azimuth ?? 0),
        pitch: Math.round(face.pitch ?? 0),
      }));

    // Assign module counts: match roof faces to design arrays by azimuth-pitch
    // Group roof faces by azimuth-pitch to handle duplicates
    const roofFacesByKey = new Map<string, number[]>();
    faces.forEach((face, idx) => {
      const key = `${face.azimuth}-${face.pitch}`;
      const group = roofFacesByKey.get(key);
      if (group) {
        group.push(idx);
      } else {
        roofFacesByKey.set(key, [idx]);
      }
    });

    roofFacesByKey.forEach((roofFaceIndices: number[], azPitchKey: string) => {
      const faceMap = azPitchGroups.get(azPitchKey);
      if (!faceMap) return;

      // Get per-face counts sorted by count descending
      const faceCounts: number[] = [];
      faceMap.forEach((count: number) => faceCounts.push(count));
      faceCounts.sort((a: number, b: number) => b - a);

      if (roofFaceIndices.length === 1) {
        // Single roof face → sum all face sub-groups
        let total = 0;
        faceMap.forEach((count: number) => { total += count; });
        faces[roofFaceIndices[0]].number_of_modules = total;
      } else {
        // Multiple roof faces share azimuth-pitch → match by modules_area
        // Sort roof faces by modules_area descending, match with counts descending
        const sorted = roofFaceIndices
          .map((idx: number) => ({ idx, area: faces[idx].modules_area }))
          .sort((a: { idx: number; area: number }, b: { idx: number; area: number }) => b.area - a.area);

        sorted.forEach((entry: { idx: number; area: number }, j: number) => {
          faces[entry.idx].number_of_modules = j < faceCounts.length ? faceCounts[j] : 0;
        });
      }
    });

    return {
      structure_name: '',
      total_area: roof.area ?? 0,
      modules_area: roof.modules_area ?? 0,
      faces,
      non_module_faces_count: nonModuleFaces.length,
      non_module_faces_area: nonModuleFacesArea,
    };
  });

  // Structures with module faces first (by total area desc), empty ones last
  details.sort((a, b) => {
    const aHasFaces = a.faces.length > 0 ? 0 : 1;
    const bHasFaces = b.faces.length > 0 ? 0 : 1;
    if (aHasFaces !== bHasFaces) return aHasFaces - bHasFaces;
    return (b.total_area || 0) - (a.total_area || 0);
  });

  return details.map((s, i) => ({ ...s, structure_name: `STRUCTURE ${i + 1}` }));
}

// --- Aurora panel rows pool extraction ---

export interface AuroraPanelPoolRow {
  id: string;
  orientation: PanelOrientation;
  panel_count: number;
  structure_index: number; // 1-indexed structure number from azimuth/pitch mapping
}

/**
 * Extracts a pool of panel rows from Aurora design.arrays[], auto-assigning
 * each to a structure via azimuth/pitch matching.
 */
export function extractAuroraPanelRowsPool(
  designSummary: Record<string, unknown> | null,
  structures: StructureRoofDetail[],
): AuroraPanelPoolRow[] {
  const arrays: Record<string, unknown>[] =
    (designSummary as Record<string, unknown> & { design?: { arrays?: Record<string, unknown>[] } })
      ?.design?.arrays ?? [];
  if (arrays.length === 0 || structures.length === 0) return [];

  // Build azimuth/pitch → structure index map from confirmed structures
  const azPitchToStructIdx = new Map<string, number>();
  structures.forEach((s, idx) => {
    for (const face of s.faces) {
      const key = `${Math.round(face.azimuth)}-${Math.round(face.pitch)}`;
      if (!azPitchToStructIdx.has(key)) {
        azPitchToStructIdx.set(key, idx);
      }
    }
  });

  // Map each array's face to a structure via azimuth/pitch
  const faceToStructIdx = new Map<number, number>();
  for (const arr of arrays) {
    const faceIdx = arr.face as number;
    if (!faceToStructIdx.has(faceIdx)) {
      const azKey = `${Math.round(arr.azimuth as number)}-${Math.round(arr.pitch as number)}`;
      faceToStructIdx.set(faceIdx, azPitchToStructIdx.get(azKey) ?? 0);
    }
  }

  const pool: AuroraPanelPoolRow[] = [];

  for (const arr of arrays) {
    const mod = arr.module as Record<string, unknown> | undefined;
    const count = (mod?.count as number) ?? 0;
    if (count <= 0) continue;

    const structIdx = faceToStructIdx.get(arr.face as number) ?? 0;

    pool.push({
      id: `apr_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      orientation: mod?.orientation === 'portrait'
        ? PanelOrientation.PORTRAIT
        : PanelOrientation.LANDSCAPE,
      panel_count: count,
      structure_index: structIdx + 1,
    });
  }

  return pool;
}

// --- Payload stripping ---

interface StrippedArray {
  module: {
    id: string;
    name: string;
    rating_stc: number;
    orientation: string;
    count: number;
  };
  azimuth: number;
  pitch: number;
  face: number;
}

interface StrippedDesignSummary {
  design: {
    design_id: string;
    bill_of_materials: AuroraBomItem[];
    arrays: StrippedArray[];
    energy_production: { annual: number };
  };
}

/**
 * Strips design_summary to only fields used by the application.
 * Removes: hourly/monthly energy data, shading, configuration, size, ground_mounted, etc.
 */
export function stripDesignSummary(raw: Record<string, unknown>): StrippedDesignSummary {
  const design = raw.design as Record<string, unknown>;

  const arrays = (design?.arrays as Record<string, unknown>[] ?? []).map((arr) => ({
    module: arr.module as StrippedArray['module'],
    azimuth: arr.azimuth as number,
    pitch: arr.pitch as number,
    face: arr.face as number,
  }));

  const energyProduction = design?.energy_production as Record<string, unknown> | undefined;

  return {
    design: {
      design_id: design?.design_id as string,
      bill_of_materials: design?.bill_of_materials as AuroraBomItem[] ?? [],
      arrays,
      energy_production: {
        annual: (energyProduction?.annual as number) ?? 0,
      },
    },
  };
}

/**
 * Strips roof_design_summary to only fields used by the application.
 * Keeps full face objects, area, and modules_area per roof.
 */
export function stripRoofDesignSummary(raw: Record<string, unknown>): Record<string, unknown> {
  const roofs = (raw.roofs as Record<string, unknown>[] ?? [])
    .slice(0, MAX_STRUCTURES)
    .map((roof) => ({
      area: roof.area,
      modules_area: roof.modules_area,
      faces: roof.faces,
    }));

  return { roofs };
}
